import { useEffect, useRef, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Edit, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { useEvents } from '../hooks/useEvents';
import { formatTimeTo12Hour } from '@/utils/timezoneUtils';
import AdminEventCleanup from '@/pages/AdminEventCleanup';
import { Pagination } from '@/components/Pagination';

export const AdminCalendarTab = () => {
  const {
    events,
    editingEvent,
    eventForm,
    setEventForm,
    fetchEvents,
    resetEventForm,
    handleEditEvent,
    saveEvent,
    deleteEvent
  } = useEvents();

  const eventFormRef = useRef(null);
  const [showCleanup, setShowCleanup] = useState(false);
  const [page, setPage] = useState(1);
  const PER_PAGE = 20;

  useEffect(() => {
    fetchEvents();
  }, []);

  const totalPages = Math.max(1, Math.ceil(events.length / PER_PAGE));
  const pagedEvents = events.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const onEdit = (event) => {
    handleEditEvent(event, eventFormRef);
  };

  const onSave = async () => {
    await saveEvent(fetchEvents);
  };

  const onDelete = async (id) => {
    await deleteEvent(id, fetchEvents);
  };

  return (
    <div className="space-y-6">
      <Card ref={eventFormRef}>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{editingEvent ? 'Edit Event' : 'Create Calendar Event'}</span>
            {editingEvent && (
              <Button variant="ghost" size="sm" onClick={resetEventForm}>
                Cancel Edit
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Event Title</Label>
            <Input
              value={eventForm.title}
              onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
              placeholder="Event Title"
            />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              value={eventForm.description}
              onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
              placeholder="Event description"
              rows={3}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={eventForm.event_date}
                onChange={(e) => setEventForm({ ...eventForm, event_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Time</Label>
              <Input
                type="time"
                value={eventForm.event_time}
                onChange={(e) => setEventForm({ ...eventForm, event_time: e.target.value })}
              />
            </div>
          </div>
          <div>
            <Label>Timezone</Label>
            <select
              value={eventForm.timezone}
              onChange={(e) => setEventForm({ ...eventForm, timezone: e.target.value })}
              className="w-full p-2 border rounded-md bg-white text-black"
            >
              <option value="America/New_York">Eastern Time (ET)</option>
              <option value="America/Chicago">Central Time (CT)</option>
              <option value="America/Denver">Mountain Time (MT)</option>
              <option value="America/Los_Angeles">Pacific Time (PT)</option>
              <option value="America/Anchorage">Alaska Time (AKT)</option>
              <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
              <option value="Europe/London">London (GMT)</option>
              <option value="Europe/Paris">Paris (CET)</option>
              <option value="Asia/Tokyo">Tokyo (JST)</option>
              <option value="Australia/Sydney">Sydney (AEST)</option>
              <option value="UTC">UTC</option>
            </select>
          </div>
          <div>
            <Label>Link (optional)</Label>
            <Input
              value={eventForm.link}
              onChange={(e) => setEventForm({ ...eventForm, link: e.target.value })}
              placeholder="https://..."
            />
          </div>
          <Button onClick={onSave} className="btn-primary w-full">
            {editingEvent ? 'Update Event' : 'Create Event'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Existing Events ({events.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No events created yet</p>
          ) : (
            <>
              <div className="space-y-3">
                {pagedEvents.map((event) => (
                <div key={event.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{event.title}</h3>
                    <p className="text-sm text-gray-600">{event.description}</p>
                    <div className="flex gap-3 mt-2 text-xs text-gray-500">
                      <span>📅 {event.event_date}</span>
                      <span>🕐 {formatTimeTo12Hour(event.event_time)}</span>
                      {event.timezone && <span>🌍 {event.timezone.split('/')[1].replace('_', ' ')}</span>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => onEdit(event)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Event?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete &quot;{event.title}&quot;.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => onDelete(event.id)}>
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
              </div>
              <Pagination
                page={page}
                totalPages={totalPages}
                onChange={setPage}
                testidPrefix="admin-calendar-pagination"
                theme="light"
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Event Cleanup Section */}
      <Card>
        <CardHeader>
          <button
            onClick={() => setShowCleanup(!showCleanup)}
            className="w-full flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            {showCleanup ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            <CardTitle>Event Cleanup</CardTitle>
          </button>
        </CardHeader>
        {showCleanup && (
          <CardContent>
            <AdminEventCleanup />
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default AdminCalendarTab;
